// HuskyHeroMascot — global floating mascot that walks in every 20 s on any
// page, holds a "Get Started Now!" sign next to its right paw, and routes
// to the Request-Info funnel on click. Stops appearing once the visitor
// either submits a lead form or clicks the sign.
import { useEffect, useState, useCallback } from "react";
import { hasLeadBeenCaptured, markLeadCaptured } from "../utils/leadCapture";
import "./HuskyHeroMascot.css";

const SHOW_INTERVAL_MS = 20_000;     // re-show cadence
const VISIBLE_MS = 10_000;            // how long it stays on screen
const FIRST_DELAY_MS = 4_000;         // delay before the very first appearance
const CTA_HREF = "/discovery-quiz?utm_source=mascot&utm_medium=hero&utm_campaign=husky-cta";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const isMobile = () =>
  typeof window !== "undefined" && window.innerWidth < 900;

export default function HuskyHeroMascot() {
  const [visible, setVisible] = useState(false);
  const [suppressed, setSuppressed] = useState(false);

  const checkSuppressed = useCallback(() => {
    if (hasLeadBeenCaptured() || prefersReducedMotion() || isMobile()) {
      setSuppressed(true);
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (checkSuppressed()) return undefined;

    let showTimer = null;
    let hideTimer = null;

    const show = () => {
      if (checkSuppressed()) return;
      setVisible(true);
      hideTimer = setTimeout(() => setVisible(false), VISIBLE_MS);
    };

    // First appearance after a short delay so the page can settle
    showTimer = setTimeout(show, FIRST_DELAY_MS);
    // Repeating cadence — every SHOW_INTERVAL_MS thereafter
    const interval = setInterval(show, SHOW_INTERVAL_MS);

    // External signal: another component fired the lead-captured event
    const onCaptured = () => {
      setSuppressed(true);
      setVisible(false);
    };
    window.addEventListener("ac:lead-captured", onCaptured);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      clearInterval(interval);
      window.removeEventListener("ac:lead-captured", onCaptured);
    };
  }, [checkSuppressed]);

  const onSignClick = () => {
    // Mark capture so the mascot doesn't re-appear after returning.
    markLeadCaptured("mascot-cta");
  };

  const onDismiss = (e) => {
    // Manual close — just hides this appearance, doesn't suppress future
    // ones. Use the lead-captured flag for permanent suppression.
    e.preventDefault();
    e.stopPropagation();
    setVisible(false);
  };

  if (suppressed || !visible) return null;

  return (
    <div className="ac-husky-mascot" data-testid="husky-hero-mascot" role="region" aria-label="Anderson College mascot">
      <a
        href={CTA_HREF}
        className="ac-husky-sign"
        data-testid="husky-mascot-cta"
        onClick={onSignClick}
        aria-label="Get started — Discovery Quiz"
      >
        <span className="ac-husky-sign-card">
          Get Started Now! <span className="ac-husky-arrow" aria-hidden="true">→</span>
        </span>
      </a>
      <img
        src="/assets/mascot/husky.png"
        alt=""
        className="ac-husky-img"
        draggable={false}
        aria-hidden="true"
      />
      <button
        type="button"
        className="ac-husky-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss mascot"
        data-testid="husky-mascot-dismiss"
      >
        ×
      </button>
    </div>
  );
}
